import * as React from 'react';
import {Mutation} from 'react-apollo';

import {BulletedList} from 'shells/bulleted-list/bulleted-list';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {RetryableError} from 'shells/flash-banner/retryable-error';
import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import ExchangeIcon from 'shells/logos/exchange.svg';
import {MessageContainer} from 'shells/message/message-container';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {TextfieldWithButtons} from 'shells/textfield/textfield-with-buttons';
import {MarketingHeader} from 'shells/typography/marketing-header';

import type {
    CalendarSync as CalendarSyncFragment,
    setExchangeUsername as setExchangeUsernameMutation,
    setExchangeUsernameVariables as setExchangeUsernameMutationVariables,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import SET_EXCHANGE_USERNAME from '../shared/graphql/mutations/set-exchange-username.graphql';
import {WidgetExchange} from '../shared/widget-exchange';

import {CalendarConnectButtons} from './calendar-connect-buttons';
import {CalendarOptions} from './calendar-options';
import {CalendarWidgetGoogle} from './calendar-widget-google';
import {CalendarWidgetOffice} from './calendar-widget-office';
import {ConflictCalendars} from './conflict-calendars';
import {CALENDAR_BULLET_POINTS} from './consts';
import {useSelectCalendarSyncCalendar} from './graphql/hooks';
import {SelectCalendarModalWrapper} from './select-calendar-modal/select-calendar-modal-wrapper';

import styles from './calendar-sync-settings.module.css';

type Props = {
    calendarSync: CalendarSyncFragment | null | undefined;
    refetchSyncStatus: () => Promise<any>;
    errorMessage: string | null | undefined;
    isEmbedded?: boolean;
};

const exchangeLogo = <ExchangeIcon width={24} />;

class ExchangeSetUsernameMutation extends Mutation<
    setExchangeUsernameMutation,
    setExchangeUsernameMutationVariables
> {}

export function CalendarSyncSettings(props: Props) {
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [successMessages, setSuccessMessages] = React.useState<React.ReactElement<any>[]>([]);
    const [enablingExchange, setEnablingExchange] = React.useState<boolean>(false);
    const [exchangeFailedErrorMessage, setExchangeFailedErrorMessage] = React.useState<
        string | null | undefined
    >(undefined);
    const [shouldShowSelectCalendarModal, setShouldShowSelectCalendarModal] =
        React.useState<boolean>(false);
    const {calendarSync} = props;

    const {selectCalendarSyncCalendar} = useSelectCalendarSyncCalendar();

    const banners = (
        <MessageContainer>
            {errorMessages.map((message) => (
                <FlashBanner
                    key={message}
                    hasBorder={true}
                    type='error'
                    onHidden={() => {
                        setErrorMessages(errorMessages.filter((msg) => msg !== message));
                    }}
                >
                    {message}
                </FlashBanner>
            ))}
            {successMessages.map((message, index) => (
                <FlashBanner
                    key={index}
                    hasBorder={true}
                    type='success'
                    onHidden={() => {
                        setSuccessMessages(successMessages.filter((msg) => msg !== message));
                    }}
                >
                    {message}
                </FlashBanner>
            ))}
        </MessageContainer>
    );

    if (!calendarSync) {
        const errorDisplay = (
            <RetryableError
                message='There was a problem getting your calendar sync settings. Please retry or contact
                support.'
                details={props.errorMessage}
                retry={props.refetchSyncStatus}
            />
        );

        const exchangeEmailForm = (
            <ExchangeSetUsernameMutation mutation={SET_EXCHANGE_USERNAME}>
                {(setExchangeUsername, {loading: isExchangeChanging}) => (
                    <TextfieldWithButtons
                        name='emailAddress'
                        title='Connect to Microsoft Exchange'
                        subtitle='Enter the email address you use with Microsoft Exchange'
                        placeholder='Email address'
                        onCancel={() => setEnablingExchange(false)}
                        onSave={(email) => {
                            // Reset error message if one was set before
                            if (exchangeFailedErrorMessage)
                                setExchangeFailedErrorMessage(undefined);

                            setExchangeUsername({variables: {username: email}})
                                .then(() => {
                                    props.refetchSyncStatus().then(() => {
                                        setEnablingExchange(false);
                                    });
                                })
                                .catch((error) => {
                                    // Currently silently failing here - this is just to reset the save button on fail
                                    setExchangeFailedErrorMessage(error.message);
                                });
                        }}
                        focusOnMount={true}
                        isSaving={isExchangeChanging}
                        errorMessage={exchangeFailedErrorMessage}
                        textfieldSize='big'
                    />
                )}
            </ExchangeSetUsernameMutation>
        );

        let mainSection = (
            <div className={styles['initial-sync-ctas']}>
                <CalendarConnectButtons
                    calendarSync={calendarSync}
                    refetchSyncStatus={props.refetchSyncStatus}
                    errorMessages={errorMessages}
                    setErrorMessages={setErrorMessages}
                    onAuthSuccess={() => {
                        if (NutClientConfig.hasImprovedEmailCalendarSyncFF) {
                            appHistory.replace({hash: '/connect-calendar'});
                        } else {
                            setShouldShowSelectCalendarModal(true);
                        }
                    }}
                />
                {NutClientConfig.integrations.exchangeEnabled ? (
                    <IntegrationWidget
                        status='initial'
                        account={null}
                        initialText='Connect to my Exchange tasks'
                        logo={exchangeLogo}
                        onConnectClick={() => {
                            setEnablingExchange(true);
                        }}
                        onDisconnectClick={() => {}}
                    />
                ) : undefined}
            </div>
        );

        if (props.errorMessage) {
            mainSection = errorDisplay;
        } else if (enablingExchange) {
            mainSection = exchangeEmailForm;
        }

        return (
            <React.Fragment>
                {banners}
                <MarketingHeader>Keep your calendar in sync with Nutshell</MarketingHeader>
                <BulletedList bullets={CALENDAR_BULLET_POINTS} />
                {mainSection}
            </React.Fragment>
        );
    }

    const {isEnabled, username, externalService, syncErrors} = calendarSync;
    const primaryCalendar = calendarSync.calendars.find((c) => c.syncType === 'PRIMARY');

    if (externalService === 'GOOGLE' || externalService === 'GOOGLE_APPS') {
        return (
            <React.Fragment>
                {banners}
                <SpacerGroup mb={32}>
                    <MarketingHeader>External calendar account</MarketingHeader>
                    <CalendarWidgetGoogle
                        isEnabled={isEnabled}
                        errors={syncErrors || []}
                        username={username}
                        externalService={externalService}
                        onAuthStart={() => {
                            setErrorMessages([]);
                        }}
                        onAuthSuccess={() => {
                            return props.refetchSyncStatus();
                        }}
                        onAuthError={(errMsg) => {
                            setErrorMessages(errorMessages.concat(errMsg));
                        }}
                        primaryCalendar={primaryCalendar}
                        appHistory={appHistory}
                        isEmbedded={props.isEmbedded}
                    />
                </SpacerGroup>
                <CalendarOptions calendarSync={calendarSync} />
                {calendarSync &&
                    calendarSync.isEnabled &&
                    calendarSync.calendars.find((calendar) => calendar.isSelected) && (
                        <ConflictCalendars
                            errorMessages={errorMessages}
                            setErrorMessages={setErrorMessages}
                        />
                    )}
                {shouldShowSelectCalendarModal && (
                    <SelectCalendarModalWrapper
                        username={calendarSync.username}
                        isOpen={shouldShowSelectCalendarModal}
                        onClose={() => setShouldShowSelectCalendarModal(false)}
                        onSave={(calendarId) => {
                            return selectCalendarSyncCalendar(calendarId);
                        }}
                        modalType='sync'
                    />
                )}
            </React.Fragment>
        );
    }

    if (externalService === 'OFFICE365') {
        return (
            <React.Fragment>
                {banners}
                <SpacerGroup mb={32}>
                    <MarketingHeader>External calendar account</MarketingHeader>
                    <CalendarWidgetOffice
                        isEnabled={isEnabled}
                        errors={syncErrors || []}
                        username={username}
                        onAuthStart={() => {
                            setErrorMessages([]);
                        }}
                        onAuthSuccess={(msg?: React.ReactElement<any> | null) => {
                            if (msg) setSuccessMessages([msg]);
                            setShouldShowSelectCalendarModal(true);

                            return props.refetchSyncStatus();
                        }}
                        onAuthError={(errMsg) => {
                            setErrorMessages(errorMessages.concat(errMsg));
                        }}
                        primaryCalendar={primaryCalendar}
                        appHistory={appHistory}
                        isEmbedded={props.isEmbedded}
                    />
                </SpacerGroup>
                <CalendarOptions calendarSync={calendarSync} />
                {calendarSync &&
                    calendarSync.isEnabled &&
                    calendarSync.calendars.find((calendar) => calendar.isSelected) && (
                        <ConflictCalendars
                            errorMessages={errorMessages}
                            setErrorMessages={setErrorMessages}
                        />
                    )}
                {shouldShowSelectCalendarModal && (
                    <SelectCalendarModalWrapper
                        username={calendarSync.username}
                        isOpen={shouldShowSelectCalendarModal}
                        onClose={() => setShouldShowSelectCalendarModal(false)}
                        onSave={(calendarId) => {
                            return selectCalendarSyncCalendar(calendarId);
                        }}
                        modalType='sync'
                    />
                )}
            </React.Fragment>
        );
    }

    if (externalService === 'EXCHANGE') {
        return (
            <SpacerGroup mb={32}>
                {banners}
                <MarketingHeader>External calendar account</MarketingHeader>
                <WidgetExchange
                    isEnabled={isEnabled}
                    username={username}
                    onAuthStart={() => {
                        setErrorMessages([]);
                    }}
                    onAuthSuccess={() => {
                        return props.refetchSyncStatus();
                    }}
                    onAuthError={(errMsg) => {
                        setErrorMessages(errorMessages.concat(errMsg));
                    }}
                />
            </SpacerGroup>
        );
    }

    return null;
}
